'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { SMSLog, getSMSLogs, getNotificationStats, sendWhatsAppNotification } from '@/lib/smsNotification';

const TYPE_LABELS: Record<string, string> = {
  booking_confirmed: 'Booking Confirmed', booking_pending: 'Booking Pending', booking_cancelled: 'Booking Cancelled',
  support_ticket_created: 'Support Ticket Created', support_ticket_updated: 'Support Ticket Updated',
  price_alert: 'Price Alert', check_in_reminder: 'Check-in Reminder', flight_update: 'Flight Update',
};

export default function SMSNotificationAdmin() {
  const [logs, setLogs] = useState<SMSLog[]>([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, pending: 0, failed: 0, bookingAlerts: 0, supportAlerts: 0, priceAlerts: 0 });
  const [showCompose, setShowCompose] = useState(false);
  const [compose, setCompose] = useState({ name: '', phone: '', message: '', type: 'booking_confirmed' as const });
  const [isSending, setIsSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  useEffect(() => { refreshData(); }, []);

  function refreshData() { setLogs(getSMSLogs()); setStats(getNotificationStats()); }

  function handleSend(e: React.FormEvent) {
    e.preventDefault();
    setIsSending(true);
    setTimeout(() => {
      sendWhatsAppNotification({ type: compose.type, recipientName: compose.name, recipientPhone: compose.phone, message: compose.message });
      setSentSuccess(true);
      setIsSending(false);
      refreshData();
      setTimeout(() => { setSentSuccess(false); setShowCompose(false); }, 2000);
    }, 600);
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Sent', value: stats.total, icon: '📨', color: 'text-foreground', bg: 'bg-surface' },
          { label: 'Booking Alerts', value: stats.bookingAlerts, icon: '✈️', color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Support Alerts', value: stats.supportAlerts, icon: '🎫', color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Price Alerts', value: stats.priceAlerts, icon: '💷', color: 'text-green-600', bg: 'bg-green-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 text-center border border-border`}>
            <p className="text-xl mb-1">{s.icon}</p>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-bold text-foreground">WhatsApp Notification Log</h3>
        <button onClick={() => setShowCompose(!showCompose)} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-green-700 transition-colors">
          <Icon name="PaperAirplaneIcon" size={16} />Send Notification
        </button>
      </div>

      {showCompose && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5">
          <h4 className="font-bold text-foreground mb-4">Send WhatsApp Notification</h4>
          {sentSuccess ? (
            <div className="text-center py-4"><p className="text-green-700 font-bold">WhatsApp opened! Message ready to send.</p></div>
          ) : (
            <form onSubmit={handleSend} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-foreground mb-1">Recipient Name *</label>
                <input type="text" required value={compose.name} onChange={e => setCompose(c => ({ ...c, name: e.target.value }))} className="w-full border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" placeholder="Customer name" />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1">Phone Number</label>
                <input type="tel" value={compose.phone} onChange={e => setCompose(c => ({ ...c, phone: e.target.value }))} className="w-full border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" placeholder="07700 900 123" />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1">Notification Type</label>
                <select value={compose.type} onChange={e => setCompose(c => ({ ...c, type: e.target.value as any }))} className="w-full border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300 bg-white">
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1">Custom Message</label>
                <input type="text" value={compose.message} onChange={e => setCompose(c => ({ ...c, message: e.target.value }))} className="w-full border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" placeholder="Optional custom message" />
              </div>
              <div className="sm:col-span-2 flex gap-3">
                <button type="submit" disabled={isSending} className="flex-1 bg-green-600 text-white py-2.5 rounded-xl font-bold text-sm hover:bg-green-700 transition-colors disabled:opacity-50">
                  {isSending ? 'Opening WhatsApp...' : 'Send via WhatsApp'}
                </button>
                <button type="button" onClick={() => setShowCompose(false)} className="px-4 py-2.5 border border-border rounded-xl font-bold text-sm hover:bg-surface transition-colors">Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground"><p className="text-sm">No notifications sent yet</p></div>
        ) : (
          logs.map(log => (
            <div key={log.id} className="flex items-start gap-3 p-3 bg-surface rounded-xl border border-border">
              <div className="flex-shrink-0">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${log.status === 'sent' ? 'bg-green-500' : log.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-foreground">{TYPE_LABELS[log.type] || log.type}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(log.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <p className="text-xs text-muted-foreground">To: {log.recipientName}{log.recipientPhone ? ` (${log.recipientPhone})` : ''}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                log.status === 'sent' ? 'bg-green-100 text-green-700' : log.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}>{log.status}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
